import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Skeleton,
  Alert,
  Chip,
  Link,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Groups as TotalTeamIcon,
  Badge as BadgedInIcon,
  Shield as CommanderIcon,
  HowToReg as HeadcounterIcon,
  SupportAgent as BackupIcon,
  Business as DirectorIcon,
  Close as ClearIcon,
} from '@mui/icons-material';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import { getIncidentDetail } from '../Services/IncidentService';
import type { Incident, IncidentRosterPerson } from '../Types/Incident';
import type { IncidentAssignment, IncidentAssignmentRoleName } from '../Types/IncidentAssignment';
import {
  groupIncidentAssignmentsByRole,
  getAssignmentRoleLabel,
  pluralizeRoleLabel,
  getRoleSubtitle,
} from '../Utils/incidentTeamHelpers';
import { appColors } from '../Config/Theme';
import IncidentDayBadgeInfo from '../Components/Elements/IncidentDayBadgeInfo';

/* ── constants ── */

const ROLE_ICON_MAP: Record<IncidentAssignmentRoleName, (size: number) => React.ReactElement> = {
  IncidentCommander: (s) => <CommanderIcon sx={{ fontSize: s }} />,
  OfficeDirector: (s) => <DirectorIcon sx={{ fontSize: s }} />,
  Headcounter: (s) => <HeadcounterIcon sx={{ fontSize: s }} />,
  BackupHeadcounter: (s) => <BackupIcon sx={{ fontSize: s }} />,
};

const AVATAR_COLORS: Record<IncidentAssignmentRoleName, string> = {
  IncidentCommander: '#1565C0',
  OfficeDirector: '#00695C',
  Headcounter: '#2E7D32',
  BackupHeadcounter: '#7B1FA2',
};

const COLLAPSE_THRESHOLD = 12;

const ALWAYS_EXPANDED_ROLES: IncidentAssignmentRoleName[] = [
  'IncidentCommander',
  'OfficeDirector',
];

const ALL_ROLES: IncidentAssignmentRoleName[] = [
  'IncidentCommander',
  'OfficeDirector',
  'Headcounter',
  'BackupHeadcounter',
];

/* ── helpers ── */

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* ── main page ── */

const IncidentTeamPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const { assignments, loading, error, refetch } = useIncidentAssignments(incidentId);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<IncidentAssignmentRoleName | null>(null);
  const [badgedInFilter, setBadgedInFilter] = useState(false);
  const [incident, setIncident] = React.useState<Incident | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!incidentId) return;
    getIncidentDetail(incidentId)
      .then(setIncident)
      .catch(() => {});
  }, [incidentId]);

  const activeAssignments = useMemo(
    () => assignments.filter((a) => a.isActive),
    [assignments],
  );

  /* Role counts for summary cards & filter chips */
  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const role of ALL_ROLES) counts[role] = 0;
    for (const a of activeAssignments) {
      counts[a.assignmentRoleName] = (counts[a.assignmentRoleName] ?? 0) + 1;
    }
    return counts;
  }, [activeAssignments]);

  /* Badged-in count */
  const badgedInCount = useMemo(
    () => activeAssignments.filter((a) => a.hasIncidentDayBadgeActivity === true).length,
    [activeAssignments],
  );

  /* Filter by role, badged-in, then search */
  const filtered = useMemo(() => {
    let result = activeAssignments;
    if (roleFilter) {
      result = result.filter((a) => a.assignmentRoleName === roleFilter);
    }
    if (badgedInFilter) {
      result = result.filter((a) => a.hasIncidentDayBadgeActivity === true);
    }
    if (!searchQuery.trim()) return result;
    const q = searchQuery.toLowerCase();
    return result.filter(
      (a) =>
        a.displayName?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.phone?.toLowerCase().includes(q) ||
        a.assignmentRoleName?.toLowerCase().includes(q) ||
        a.viewType?.toLowerCase().includes(q) ||
        a.viewValue?.toLowerCase().includes(q),
    );
  }, [activeAssignments, roleFilter, badgedInFilter, searchQuery]);

  const groups = useMemo(() => groupIncidentAssignmentsByRole(filtered), [filtered]);

  /* Determine initial expansion state */
  const isSectionExpanded = (roleName: IncidentAssignmentRoleName): boolean => {
    if (expandedSections[roleName] !== undefined) return expandedSections[roleName];
    if (roleFilter || badgedInFilter) return true; // expand all when filtering
    if (searchQuery.trim()) return true;
    if (ALWAYS_EXPANDED_ROLES.includes(roleName)) return true;
    return activeAssignments.length <= COLLAPSE_THRESHOLD;
  };

  const toggleSection = (roleName: IncidentAssignmentRoleName) => {
    setExpandedSections((prev) => ({ ...prev, [roleName]: !isSectionExpanded(roleName) }));
  };

  /* Reset explicit expansions when filter/search changes */
  React.useEffect(() => {
    setExpandedSections({});
  }, [roleFilter, badgedInFilter, searchQuery]);

  const displayName = incident?.incidentName ?? '';
  const totalCount = activeAssignments.length;

  const handleRoleFilterClick = (role: IncidentAssignmentRoleName | null) => {
    setRoleFilter(role);
  };

  const clearSearch = () => setSearchQuery('');

  /* Summary cards config */
  const summaryCards: { key: string; label: string; count: number; role: IncidentAssignmentRoleName | null; renderIcon: (size: number) => React.ReactElement; color: string }[] = ALL_ROLES
    .filter((role) => roleCounts[role] > 0)
    .map((role) => ({
      key: role,
      label: pluralizeRoleLabel(role, roleCounts[role]),
      count: roleCounts[role],
      role,
      renderIcon: ROLE_ICON_MAP[role],
      color: AVATAR_COLORS[role],
    }));
  summaryCards.push({ key: 'total', label: 'Total Team', count: totalCount, role: null, renderIcon: (s) => <TotalTeamIcon sx={{ fontSize: s }} />, color: appColors.text.heading });
  summaryCards.push({ key: 'badgedIn', label: 'Badged In', count: badgedInCount, role: null, renderIcon: (s) => <BadgedInIcon sx={{ fontSize: s }} />, color: '#2E7D32' });

  /* Role filter chips */
  const filterChips: { key: string; label: string; count: number; role: IncidentAssignmentRoleName | null }[] = [
    { key: 'all', label: 'All', count: totalCount, role: null },
    ...ALL_ROLES
      .filter((role) => roleCounts[role] > 0)
      .map((role) => ({
        key: role,
        label: getAssignmentRoleLabel(role),
        count: roleCounts[role],
        role,
      })),
  ];

  const handleBadgedInClick = () => setBadgedInFilter((prev) => !prev);

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1150, mx: 'auto', width: '100%' }}>
      <>
      <AppBreadcrumbs items={[
        { label: 'Incidents', path: '/' },
        { label: displayName || 'Incident', path: `/incidents/${incidentId}/incidentdashboard` },
        { label: 'Team' },
      ]} />

      {/* Page title */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Incident Team
        </Typography>
        <Typography variant="body2" sx={{ color: appColors.text.caption, mt: 0.25 }}>
          Team assignments and contact information for this incident.
        </Typography>
      </Box>

      {/* Incident info bar */}
      {incident && (
        <Box sx={{ mb: 2 }}>
          <IncidentInfoBar
            incidentName={incident.incidentName}
            incidentType={incident.incidentType}
            location={incident.location}
            startedAtUtc={incident.startedAtUtc}
            status={incident.status}
          />
        </Box>
      )}

      {/* ── Summary cards ── */}
      {!loading && !error && totalCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          {summaryCards.map((card) => {
            const isSelected = card.key === 'badgedIn' ? badgedInFilter : (!badgedInFilter && roleFilter === card.role);
            const handleCardClick = () => {
              if (card.key === 'badgedIn') {
                setBadgedInFilter((prev) => !prev);
              } else {
                handleRoleFilterClick(card.role);
                setBadgedInFilter(false);
              }
            };
            return (
              <Paper
                key={card.key}
                elevation={0}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`${card.label}: ${card.count}. Click to filter.`}
                onClick={handleCardClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick();
                  }
                }}
                sx={{
                  flex: '1 1 140px',
                  minWidth: 130,
                  maxWidth: 220,
                  px: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  border: isSelected
                    ? `2px solid ${card.color}`
                    : `1px solid ${appColors.border.subtle}`,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  '&:hover': { boxShadow: appColors.shadow.cardHover, borderColor: card.color },
                }}
              >
                <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ color: card.color, display: 'flex' }}>
                    {card.renderIcon(20)}
                  </Box>
                  <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {card.label}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: appColors.text.heading }}>
                  {card.count}
                </Typography>
                </>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ── Role filter chips ── */}
      {!loading && !error && totalCount > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          {filterChips.map((chip) => {
            const isActive = !badgedInFilter && roleFilter === chip.role;
            return (
              <Chip
                key={chip.key}
                label={`${chip.label} ${chip.count}`}
                size="small"
                clickable
                onClick={() => { handleRoleFilterClick(chip.role); setBadgedInFilter(false); }}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 28,
                  bgcolor: isActive ? appColors.primary.main : appColors.surface.inactive,
                  color: isActive ? '#FFFFFF' : appColors.text.body,
                  '&:hover': { bgcolor: isActive ? appColors.primary.dark : appColors.border.default },
                }}
              />
            );
          })}
          {badgedInCount > 0 && (
            <Chip
              key="badgedIn"
              label={`Badged In ${badgedInCount}`}
              size="small"
              clickable
              onClick={handleBadgedInClick}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 28,
                bgcolor: badgedInFilter ? '#2E7D32' : appColors.surface.inactive,
                color: badgedInFilter ? '#FFFFFF' : appColors.text.body,
                '&:hover': { bgcolor: badgedInFilter ? '#1B5E20' : appColors.border.default },
              }}
            />
          )}
        </Box>
      )}

      {/* ── Search ── */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search team by name, phone, email, or role..."
          aria-label="Search team by name, phone, email, or role"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
            },
          }}
        />
      </Box>

      {/* Search results count */}
      {searchQuery.trim() && !loading && !error && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: appColors.text.caption }}>
          Showing {filtered.length} of {roleFilter ? roleCounts[roleFilter] : totalCount} team members
        </Typography>
      )}

      {/* ── Loading ── */}
      {loading && (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1.5, borderRadius: '12px' }} />
          ))}
        </Box>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ borderRadius: '12px' }}
          action={
            <Button size="small" onClick={refetch}>Retry</Button>
          }
        >
          Incident team could not be loaded.
        </Alert>
      )}

      {/* ── Empty / no results ── */}
      {!loading && !error && filtered.length === 0 && (
        <Box sx={{ pt: 4, textAlign: 'center' }}>
          {searchQuery.trim() ? (
            <>
              <Typography variant="body2" sx={{ color: appColors.text.placeholder, mb: 1 }}>
                No team members match &ldquo;{searchQuery}&rdquo;.
              </Typography>
              <Button size="small" variant="outlined" onClick={clearSearch}>
                Clear search
              </Button>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: appColors.text.placeholder }}>
              No incident team assigned.
            </Typography>
          )}
        </Box>
      )}

      {/* ── Role sections ── */}
      {!loading && !error && groups.map((group) => {
        const expanded = isSectionExpanded(group.roleName);
        return (
          <Paper
            key={group.roleName}
            elevation={0}
            sx={{
              border: `1px solid ${appColors.border.subtle}`,
              borderRadius: '12px',
              mb: 1.5,
              overflow: 'hidden',
            }}
          >
            {/* Section header */}
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-label={`${pluralizeRoleLabel(group.roleName, group.assignments.length)}, ${group.assignments.length} people`}
              onClick={() => toggleSection(group.roleName)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSection(group.roleName);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                bgcolor: appColors.surface.card,
                '&:hover': { bgcolor: '#FAFBFC' },
                borderBottom: expanded ? `1px solid ${appColors.border.subtle}` : 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: AVATAR_COLORS[group.roleName], display: 'flex' }}>
                  {ROLE_ICON_MAP[group.roleName](22)}
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: appColors.text.heading, lineHeight: 1.3 }}>
                      {pluralizeRoleLabel(group.roleName, group.assignments.length)}
                    </Typography>
                    <Chip
                      label={`${group.assignments.length} ${group.assignments.length === 1 ? 'person' : 'people'}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: appColors.surface.inactive,
                        color: appColors.text.body,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: appColors.text.caption, lineHeight: 1.3 }}>
                    {getRoleSubtitle(group.roleName)}
                  </Typography>
                </Box>
              </Box>
              {expanded
                ? <ExpandLessIcon sx={{ color: appColors.text.caption }} />
                : <ExpandMoreIcon sx={{ color: appColors.text.caption }} />
              }
            </Box>

            {/* Section body */}
            <Collapse in={expanded}>
              <Box>
                {group.assignments.map((a, idx) => (
                  <TeamMemberRow
                    key={a.incidentAssignmentId}
                    assignment={a}
                    avatarColor={AVATAR_COLORS[group.roleName]}
                    isLast={idx === group.assignments.length - 1}
                  />
                ))}
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {/* Future: add team assignment management when API supports it. */}
      </>
    </Box>
  );
};

/* ── Team member contact row ── */

interface TeamMemberRowProps {
  assignment: IncidentAssignment;
  avatarColor: string;
  isLast: boolean;
}

const TeamMemberRow: React.FC<TeamMemberRowProps> = ({ assignment: a, avatarColor, isLast }) => {
  const roleLabel = getAssignmentRoleLabel(a.assignmentRoleName, a.assignmentRole);
  const scopeParts: string[] = [];
  if (a.viewType && a.viewValue) scopeParts.push(a.viewValue);

  const isBadgedIn = a.hasIncidentDayBadgeActivity === true;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: isLast ? 'none' : `1px solid ${appColors.border.light}`,
        bgcolor: isBadgedIn ? '#E8F5E9' : undefined,
        '&:hover': { bgcolor: isBadgedIn ? '#C8E6C9' : '#FAFBFC' },
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 38,
          height: 38,
          bgcolor: avatarColor,
          fontWeight: 600,
          fontSize: '0.8rem',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        {getInitials(a.displayName)}
      </Avatar>

      {/* Name & contact info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            noWrap
            title={a.displayName}
            sx={{ fontWeight: 700, color: appColors.text.heading, fontSize: '0.9375rem' }}
          >
            {a.displayName}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: `${avatarColor}14`,
              color: avatarColor,
            }}
          />
          {scopeParts.length > 0 && (
            <Typography variant="caption" sx={{ color: appColors.text.caption }}>
              {scopeParts.join(' · ')}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.15 }}>
          {a.email && (
            <Link
              href={`mailto:${a.email}`}
              underline="hover"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ color: appColors.text.caption, '&:hover': { color: appColors.primary.main } }}
            >
              {a.email}
            </Link>
          )}
          {a.email && a.phone && <Box component="span">·</Box>}
          {a.phone && (
            <Link
              href={`tel:${a.phone}`}
              underline="hover"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ color: appColors.text.caption, '&:hover': { color: appColors.primary.main } }}
            >
              {a.phone}
            </Link>
          )}
        </Typography>
        {/* Badge activity */}
        {(a.lastIncidentDayBadgeActivity || a.lobbyTrackActivity || a.hasIncidentDayBadgeActivity !== undefined || a.rosterSource || a.isAssignedToIncidentOffice) && (
          <Box sx={{ mt: 0.25 }}>
            <IncidentDayBadgeInfo person={a as unknown as IncidentRosterPerson} />
          </Box>
        )}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {a.phone && (
          <Tooltip title={`Call ${a.displayName}`} arrow>
            <IconButton
              size="small"
              component="a"
              href={`tel:${a.phone}`}
              aria-label={`Call ${a.displayName} at ${a.phone}`}
              sx={{ color: appColors.primary.main }}
            >
              <PhoneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        {a.email && (
          <Tooltip title={`Email ${a.displayName}`} arrow>
            <IconButton
              size="small"
              component="a"
              href={`mailto:${a.email}`}
              aria-label={`Email ${a.displayName}`}
              sx={{ color: appColors.primary.main }}
            >
              <EmailIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default IncidentTeamPage;
